import {Easing, Tween} from '@tweenjs/tween.js';
import {Group, Line, Mesh, Scene, Vector3} from 'three';

interface Properties {
    easing?: any;
    duration?: number;
    onComplete?: any;
    onUpdate?: any;
    onRepeat?: any;
    radius?: number;
    maxDuration?: number;
    boundsX?: number[];
    boundsY?: number[];
    boundsZ?: number[];
    rng?: any;
}

export function createLinearTween(
        object: Mesh | Line | Group,
        propertyKey: string,
        targetVector: Vector3,
        properties: Properties = {}
): Tween<{ x: number }> {
    const tween = createBaseTween(properties);
    const {
        onComplete,
    } = properties;

    const startVector = new Vector3();
    tween.onStart(() => {
        // @ts-ignore
        startVector.copy(object[propertyKey])
    });

    tween.onUpdate(valueObject => {
        const t = valueObject.x;
        const x = (1 - t) * startVector.x + t * targetVector.x;
        const y = (1 - t) * startVector.y + t * targetVector.y;
        const z = (1 - t) * startVector.z + t * targetVector.z;
        // @ts-ignore
        object[propertyKey].set(x, y, z);
    });

    if (onComplete) {
        tween.onComplete(onComplete);
    }

    return tween;
}

export function createPropertyTween(
        object: any,
        propertyKey: string,
        targetValue: number,
        properties: Properties = {})
        : Tween<{
    x: number
}> {
    const tween = createBaseTween(properties);

    const {
        onComplete,
    } = properties;
    let startValue: number;

    tween.onStart(() => {
        startValue = object[propertyKey]
    });

    tween.onUpdate(valueObject => {
        const t = valueObject.x;
        object[propertyKey] = (1 - t) * startValue + t * targetValue
    });

    if (onComplete) {
        tween.onComplete(onComplete);
    }

    return tween;
}

export function createBaseTween(properties: Properties): Tween<{ x: number }> {
    const {
        easing = Easing.Linear.None,
        duration = 30000,
    } = properties;

    const xObject = {x: 0};
    const tween = new Tween(xObject).to({x: 1}, duration);

    tween.easing(easing);
    return tween;
}

export function createValueTween(value: number, targetValue: number, properties: Properties = {}): Tween<{
    x: number
}> {

    const tween = createBaseTween(properties);
    const {
        onUpdate,
        onComplete,
    } = properties;

    tween.onUpdate(valueObject => {
        const t = valueObject.x;
        if (onUpdate) {
            onUpdate((1 - t) * value + t * targetValue);
        }
    });

    if (onComplete) {
        tween.onComplete(onComplete);
    }

    return tween;
}

export function createMoveToTween(
        scene: Scene,
        mesh: Mesh | Line | Group,
        targetPosition: Vector3,
        options: any): Tween<{ x: number }> {

    return createLinearTween(mesh, "position", targetPosition, {
        onComplete: () => {
            scene.remove(mesh);
        },
        ...options
    })
}

const MAX_D = 600;

function generateRandom(bounds: number[] = [-MAX_D / 2, MAX_D / 2]): number {
    return bounds[0] + Math.random() * (bounds[1] - bounds[0]);
}

export function createMoveToRandomTween(mesh: any, options: Properties = {}): Tween<{ x: number }> {
    const {
        easing = Easing.Linear.None,
        duration = 30000,
        onComplete,
        onRepeat,
        radius,
        maxDuration = 2000,
        boundsX,
        boundsY,
        boundsZ,
        rng,
    } = options;

    const xObject = {x: 0};
    const tween = new Tween(xObject).to({x: 1}, duration);

    tween.easing(easing);

    const targetVector = new Vector3();
    const startVector = new Vector3();


    const setVectors = () => {
        startVector.copy(mesh.position);
        const dVec = new Vector3(generateRandom(boundsX), generateRandom(boundsY), generateRandom(boundsZ));
        targetVector.copy(dVec);
        tween.duration(rng ? rng(maxDuration) : maxDuration * Math.random());
        if (radius) {
            const ratio = targetVector.length() / radius;
            if (ratio > 1) {
                targetVector.divideScalar(ratio);
            }
        }

    }

    tween.onStart(setVectors);
    tween.onRepeat(() => {
        setVectors();
        if (onRepeat) {
            onRepeat()
        }
    });

    tween.onUpdate(valueObject => {
        const t = valueObject.x;
        const x = (1 - t) * startVector.x + t * targetVector.x;
        const y = (1 - t) * startVector.y + t * targetVector.y;
        const z = (1 - t) * startVector.z + t * targetVector.z;
        mesh.position.set(x, y, z);
    });

    if (onComplete) {
        tween.onComplete(onComplete);
    }
    return tween;

}


function createScaleTween(mesh: any, targetScale: Vector3, options: any): Tween<{ x: number }> {
    return createLinearTween(mesh, "scale", targetScale, {duration: 1000, ...options})
}

export function createLoopScaleTween(mesh: any, options: any): Tween<{ x: number }> {
    const grow = createScaleTween(mesh, new Vector3(1.4, 1.4, 1.4), options)
    const shrink = createScaleTween(mesh, new Vector3(1, 1, 1), options)
    grow.chain(shrink)
    shrink.chain(grow);
    return grow;
}

export function createLoopTween(onUpdate: any, properties: Properties = {}): Tween<{ x: number }> {
    const {
        duration = 300,
        ...rest
    } = properties;

    const grow = createValueTween(0, 1, {onUpdate, duration, ...rest});
    const shrink = createValueTween(1, 0, {onUpdate, duration, ...rest});

    grow.onUpdate(valueObject => {
        onUpdate(valueObject.x)
    });
    grow.onUpdate(valueObject => {
        onUpdate(valueObject.x)
    });

    grow.chain(shrink)
    shrink.chain(grow);
    return grow;
}
